/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import * as yup from 'yup';
import { sanitizeValidation } from '../../common/utils/sanitize';

/**
 * Here mentioned users feature add user validations
 */
const forbiddenUsernames = ['user', 'users', 'admin', 'admins', 'root', 'preferred'];
//
export const addUserValidation = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required('Email Address is a required field')
    .email('Email address must be valid')
    .test((value, e) => sanitizeValidation(value, e.path)),
  username: yup
    .string()
    .trim()
    .required('Username is a required field')
    .matches(/^[a-zA-Z0-9_.@]*$/, 'Can contain only Alphabets, numbers . and @')
    .max(20, 'Username should not have more than 20 characters')
    .test('is-not-forbidden', (value) => {
      if (value && forbiddenUsernames.includes(value.toLowerCase())) {
        return new yup.ValidationError(`Username cannot be '${value}'`, null, 'username');
      }
      return true;
    })
    .test((value, e) => sanitizeValidation(value, e.path)),
  firstName: yup
    .string()
    .trim()
    .required('First Name is a required field')
    .test((value, e) => sanitizeValidation(value, e.path)),
  lastName: yup
    .string()
    .trim()
    .required('Last Name is a required field')
    .test((value, e) => sanitizeValidation(value, e.path)),
  hotels: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string(),
        label: yup.string(),
      })
    )
    .min(1, 'Hotels is a required field'),
  password: yup
    .string()
    .trim()
    .required('Password is a required field')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
      'Password should contain at least 8 characters with One Uppercase, One Lowercase and One Number'
    ),
});
/**
 * Here mentioned users feature update user validations
 */
export const updateUserValidation = (isOtpSent = false) => yup.object().shape({
  email: yup
    .string()
    .trim()
    .required('Email Address is a required field')
    .email('Email address must be valid')
    .test((value, e) => sanitizeValidation(value, e.path)),
  firstName: yup
    .string()
    .trim()
    .required('First Name is a required field')
    .test((value, e) => sanitizeValidation(value, e.path)),
  lastName: yup
    .string()
    .trim()
    .required('Last Name is a required field')
    .test((value, e) => sanitizeValidation(value, e.path)),
  userHotels: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string(),
        label: yup.string(),
      })
    )
    .min(1, 'User Hotels is a required field'),
  isCustomLandingPageEnabled: yup.boolean(),
  landingPage: yup.string().when('isCustomLandingPageEnabled', {
    is: true,
    then: yup
      .string()
      .required('Landing Page is a required field when Select Custom Landing Page is checked'),
  }),
  ...(isOtpSent && {
      otp:
       yup.string().trim().required("OTP is required")
       .matches(/^\d{6}$/, 'OTP must be exactly 6 digits'),
    }),
});
/**
 * Here mentioned users feature update password validations
 */
export const updatePasswordValidation = yup.object().shape({
  password: yup
    .string()
    .trim()
    .required('New password is a required field')
    .min(8, 'New password must contain at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]/,
      'New password should contain One Uppercase, One Lowercase and One Number'
    ),
  currentPassword: yup.string().trim().required('Current password is a required field'),
});
/**
 * Here mentioned users feature update password validations
 */
export const presetDatesValidation = yup.object().shape({
  presetName: yup
    .string()
    .trim()
    .matches(
      /^[a-zA-Z0-9_ ]+$/,
      'Preset name should contain only alphanumeric characters, underscores, and spaces',
      {
        excludeEmptyString: true,
      }
    )
    .matches(
      /^[a-zA-Z0-9_]+(?:\s[a-zA-Z0-9_]+)*$/,
      'Ensure there is at most one space between words',
      {
        excludeEmptyString: true,
      }
    )
    .max(20, 'Preset name must be at most 20 characters')
    .required('Preset name is a required field')
    .test((value, e) => sanitizeValidation(value, e.path)),
});

/**
 * Here mentioned users feature update hotel metadata validations
 */
export const hotelMetadataValidation = yup.object().shape({
  name: yup
    .string()
    .required('Hotel Name is a required field')
    .test((value, e) => sanitizeValidation(value, e.path)),
  chainName: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  chainId: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  databricksHotelId: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  displayName: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  timezone: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  city: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  state: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  country: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  hotelCode: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  ibe: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  interface: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  phram: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  phrdnName: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  pms: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  postalCode: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  propertyLatitude: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  propertyLongitude: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
  streetAddress: yup.string().test((value, e) => sanitizeValidation(value, e.path)),
});
